'use client'

import { forwardRef, ReactNode, useState, useMemo } from 'react'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Tabs Component
 * 
 * Enhanced tabs implementing BIRHAUS 4-3-1 Rule:
 * - Maximum 4 tabs for optimal cognitive load
 * - Spanish-first labeling
 * - Progressive disclosure for additional tabs
 * - Accessibility-first design (WCAG AA+)
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausTabItem {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  content: ReactNode
  disabled?: boolean
  icon?: ReactNode
  badge?: string | number
  cognitiveWeight?: number // 1-10 scale for tab complexity
}

export interface BirhausTabsProps {
  // Tab data
  tabs: BirhausTabItem[]
  
  // State
  activeTab?: string
  onTabChange?: (tabId: string) => void
  
  // BIRHAUS 4-3-1 Rule enforcement
  maxVisibleTabs?: number
  showCognitiveWarning?: boolean
  
  // Layout variants
  variant?: 'default' | 'pills' | 'underline' | 'bordered'
  size?: 'sm' | 'md' | 'lg'
  orientation?: 'horizontal' | 'vertical'
  
  // Behavior
  activateOnHover?: boolean
  lazy?: boolean // Lazy load tab content
  
  // Overflow handling
  overflowBehavior?: 'dropdown' | 'scroll' | 'wrap'
  
  // Content layout
  contentPadding?: boolean
  
  // Styling
  className?: string
  tabListClassName?: string
  tabClassName?: string
  contentClassName?: string
}

export const BirhausTabs = forwardRef<HTMLDivElement, BirhausTabsProps>(({
  // Data
  tabs,
  
  // State
  activeTab,
  onTabChange,
  
  // BIRHAUS settings
  maxVisibleTabs = 4,
  showCognitiveWarning = true,
  
  // Layout
  variant = 'default',
  size = 'md',
  orientation = 'horizontal',
  
  // Behavior
  activateOnHover = false,
  lazy = false,
  
  // Overflow
  overflowBehavior = 'dropdown',
  
  // Content
  contentPadding = true,
  
  // Styling
  className,
  tabListClassName,
  tabClassName,
  contentClassName
}, ref) => {
  
  // BIRHAUS: State management
  const [internalActiveTab, setInternalActiveTab] = useState(tabs[0]?.id || '')
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  
  const currentActiveTab = activeTab !== undefined ? activeTab : internalActiveTab
  const isControlled = activeTab !== undefined
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  // BIRHAUS: Calculate visible and overflow tabs
  const { visibleTabs, overflowTabs, cognitiveLoad } = useMemo(() => {
    const enabledTabs = tabs.filter(tab => !tab.disabled)
    
    // Sort by cognitive weight (lower weight = higher priority)
    const sortedTabs = [...enabledTabs].sort((a, b) => {
      const aWeight = a.cognitiveWeight || 5
      const bWeight = b.cognitiveWeight || 5
      return aWeight - bWeight
    })
    
    const visible = sortedTabs.slice(0, maxVisibleTabs)
    const overflow = sortedTabs.slice(maxVisibleTabs)
    
    const load = visible.length > 4 ? 'high' : 
                 visible.length > 3 ? 'medium' : 'low'
    
    return {
      visibleTabs: visible,
      overflowTabs: overflow,
      cognitiveLoad: load
    }
  }, [tabs, maxVisibleTabs])
  
  // BIRHAUS: Warn about cognitive overload
  if (showCognitiveWarning && visibleTabs.length > 4) {
    console.warn(`BIRHAUS 4-3-1 Rule Warning: Tabs show ${visibleTabs.length} items (recommended: max 4)`)
  }
  
  // BIRHAUS: Handle tab change
  const handleTabChange = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId)
    if (tab && !tab.disabled) {
      if (!isControlled) {
        setInternalActiveTab(tabId)
      }
      onTabChange?.(tabId)
      setShowOverflowMenu(false)
    }
  }
  
  // BIRHAUS: Handle hover activation
  const handleTabHover = (tabId: string) => {
    if (activateOnHover) {
      handleTabChange(tabId)
    }
  }
  
  // BIRHAUS: Get active tab content
  const activeTabData = tabs.find(tab => tab.id === currentActiveTab)
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: {
      tab: 'px-3 py-1.5 text-sm',
      content: 'p-3',
      badge: 'text-xs px-1.5 py-0.5'
    },
    md: {
      tab: 'px-4 py-2 text-base',
      content: 'p-4',
      badge: 'text-xs px-2 py-1'
    },
    lg: {
      tab: 'px-6 py-3 text-lg',
      content: 'p-6',
      badge: 'text-sm px-2.5 py-1'
    }
  }
  
  const variantClasses = {
    default: {
      tabList: 'border-b border-gray-200',
      tab: 'border-b-2 border-transparent hover:border-gray-300',
      activeTab: 'border-blue-600 text-blue-600',
      inactiveTab: 'text-gray-500 hover:text-gray-700'
    },
    pills: {
      tabList: 'bg-gray-100 rounded-lg p-1',
      tab: 'rounded-md transition-colors',
      activeTab: 'bg-white text-gray-900 shadow-sm',
      inactiveTab: 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
    },
    underline: {
      tabList: '',
      tab: 'border-b-2 border-transparent',
      activeTab: 'border-blue-600 text-blue-600',
      inactiveTab: 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
    },
    bordered: {
      tabList: 'border border-gray-200 rounded-t-lg',
      tab: 'border-r border-gray-200 last:border-r-0',
      activeTab: 'bg-white text-gray-900 border-b-white',
      inactiveTab: 'bg-gray-50 text-gray-600 hover:bg-gray-100'
    }
  }
  
  const orientationClasses = {
    horizontal: {
      container: 'w-full',
      tabList: 'flex',
      content: 'w-full'
    },
    vertical: {
      container: 'flex gap-4',
      tabList: 'flex flex-col min-w-48',
      content: 'flex-1'
    }
  }
  
  const currentSize = safeAccess(sizeClasses, size, sizeClasses.md)
  const currentVariant = safeAccess(variantClasses, variant, variantClasses.default)
  const currentOrientation = safeAccess(orientationClasses, orientation, orientationClasses.horizontal)
  
  const renderTab = (tab: BirhausTabItem, isActive: boolean) => {
    const displayLabel = resolveText(tab.labelEs, tab.labelEn, tab.label)
    
    return (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        onMouseEnter={() => handleTabHover(tab.id)}
        disabled={tab.disabled}
        className={cn(
          // Base styles
          'relative inline-flex items-center gap-2 font-medium transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
          
          // Size
          currentSize.tab,
          
          // Variant
          currentVariant.tab,
          
          // State
          isActive ? currentVariant.activeTab : currentVariant.inactiveTab,
          
          // Disabled
          tab.disabled && 'opacity-50 cursor-not-allowed',
          
          // Custom classes
          tabClassName
        )}
        role="tab"
        aria-selected={isActive}
        aria-controls={`tabpanel-${tab.id}`}
        id={`tab-${tab.id}`}
        data-birhaus-component="tab"
        data-active={isActive}
      >
        {/* Icon */}
        {tab.icon && (
          <span className="flex-shrink-0">
            {tab.icon}
          </span>
        )}
        
        {/* Label */}
        <span>{displayLabel}</span>
        
        {/* Badge */}
        {tab.badge && (
          <span className={cn(
            'inline-flex items-center justify-center rounded-full bg-gray-200 text-gray-800',
            currentSize.badge,
            isActive && 'bg-blue-100 text-blue-800'
          )}>
            {tab.badge}
          </span>
        )}
      </button>
    )
  }
  
  return (
    <div 
      ref={ref}
      className={cn(currentOrientation.container, className)}
      data-birhaus-component="tabs"
      data-cognitive-load={cognitiveLoad}
      data-orientation={orientation}
    >
      {/* BIRHAUS: Cognitive load warning */}
      {showCognitiveWarning && cognitiveLoad === 'high' && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <span className="text-sm text-yellow-800">
            Mostrando {visibleTabs.length} pestañas (máximo recomendado: 4). 
            {overflowTabs.length > 0 && ` ${overflowTabs.length} pestañas en menú desplegable.`}
          </span>
        </div>
      )}
      
      {/* Tab List */}
      <div 
        className={cn(
          currentOrientation.tabList,
          currentVariant.tabList,
          tabListClassName
        )}
        role="tablist"
        aria-orientation={orientation}
      >
        {/* Visible tabs */}
        {visibleTabs.map(tab => renderTab(tab, tab.id === currentActiveTab))}
        
        {/* Overflow menu */}
        {overflowTabs.length > 0 && overflowBehavior === 'dropdown' && (
          <div className="relative">
            <button
              onClick={() => setShowOverflowMenu(!showOverflowMenu)}
              className={cn(
                'inline-flex items-center gap-2 font-medium transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                currentSize.tab,
                currentVariant.tab,
                currentVariant.inactiveTab
              )}
            >
              <span>Más</span>
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {/* Dropdown menu */}
            {showOverflowMenu && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {overflowTabs.map(tab => {
                  const displayLabel = resolveText(tab.labelEs, tab.labelEn, tab.label)
                  const isActive = tab.id === currentActiveTab
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      disabled={tab.disabled}
                      className={cn(
                        'w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50',
                        'focus:outline-none focus:bg-gray-50',
                        isActive && 'bg-blue-50 text-blue-600',
                        tab.disabled && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {tab.icon}
                      <span>{displayLabel}</span>
                      {tab.badge && (
                        <span className="ml-auto text-xs bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded-full">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Tab Content */}
      <div 
        className={cn(
          currentOrientation.content,
          contentPadding && currentSize.content,
          contentClassName
        )}
      >
        {activeTabData && (
          <div
            role="tabpanel"
            id={`tabpanel-${activeTabData.id}`}
            aria-labelledby={`tab-${activeTabData.id}`}
            tabIndex={0}
          >
            {lazy ? (
              // Lazy load: only render active tab content
              activeTabData.content
            ) : (
              // Render all content but hide inactive
              tabs.map(tab => (
                <div
                  key={tab.id}
                  className={tab.id === currentActiveTab ? 'block' : 'hidden'}
                  role="tabpanel"
                  id={`tabpanel-${tab.id}`}
                  aria-labelledby={`tab-${tab.id}`}
                  tabIndex={tab.id === currentActiveTab ? 0 : -1}
                >
                  {tab.content}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      
      {/* Click overlay to close overflow menu */}
      {showOverflowMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowOverflowMenu(false)}
        />
      )}
    </div>
  )
})

BirhausTabs.displayName = 'BirhausTabs'

// BIRHAUS: Individual Tab component for composable usage
export interface BirhausTabProps {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  disabled?: boolean
  icon?: ReactNode
  badge?: string | number
  cognitiveWeight?: number
  children: ReactNode
}

export const BirhausTab = ({ children }: BirhausTabProps) => {
  return <>{children}</>
}

BirhausTab.displayName = 'BirhausTab'

// BIRHAUS: Hook for managing tabs state
export function useBirhausTabs(initialActiveTab?: string) {
  const [activeTab, setActiveTab] = useState(initialActiveTab || '')
  const [tabHistory, setTabHistory] = useState<string[]>([])
  
  const changeTab = (tabId: string) => {
    setTabHistory(prev => [...prev.filter(id => id !== tabId), activeTab].filter(Boolean))
    setActiveTab(tabId)
  }
  
  const goBack = () => {
    if (tabHistory.length > 0) {
      const previousTab = tabHistory[tabHistory.length - 1]
      setTabHistory(prev => prev.slice(0, -1))
      setActiveTab(previousTab)
      return previousTab
    }
    return null
  }
  
  const reset = (defaultTab: string) => {
    setActiveTab(defaultTab)
    setTabHistory([])
  }
  
  return {
    activeTab,
    setActiveTab: changeTab,
    tabHistory,
    goBack,
    reset
  }
}

// BIRHAUS: Helper to create tab items with Spanish-first labels
export function createBirhausTabItem(
  id: string,
  labelEs: string,
  content: ReactNode,
  options?: {
    labelEn?: string
    icon?: ReactNode
    badge?: string | number
    disabled?: boolean
    cognitiveWeight?: number
  }
): BirhausTabItem {
  return {
    id,
    label: labelEs,
    labelEs,
    labelEn: options?.labelEn,
    content,
    icon: options?.icon,
    badge: options?.badge,
    disabled: options?.disabled || false,
    cognitiveWeight: options?.cognitiveWeight || 5
  }
}

// Components and utilities are exported via individual export declarations above